import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';
import ClientPinBlock from '../components/ClientPinBlock';
import CryptoSignaturePanel from '../components/CryptoSignaturePanel';
import { isMotorPolicy, openMotorCertificatePrint, printPayloadFromPolicy } from '../pulsePrint';
import WalletPayBox from '../components/WalletPayBox';
import OutreachButtons from '../components/OutreachButtons';
import { openReceiptPrint, recordPremiumPayment } from '../payments';
import { attestationToken, verifyUrl } from '../attestation';
import { certificateMessage, useTenantWallets } from '../wallets';
import { coercePaymentMethod, defaultPaymentMethod, useTenantFeatures } from '../tenantFeatures';

function formatDate(value) {
    if (!value) return '—';
    const d = value && typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function statusClass(status) {
    const s = String(status || '').toLowerCase();
    if (s === 'active') return 'bd-badge bd-badge-active';
    if (s === 'expired' || s === 'lapsed' || s === 'cancelled') return 'bd-badge bd-badge-inactive';
    return 'bd-badge bd-badge-pending';
}

function classKey(p) {
    return String(p.policyType || p.insuranceType || p.categoryName || 'OTHER').toUpperCase();
}

function vinOf(p) {
    return (p.vehicle && (p.vehicle.vin || p.vehicle.VIN || p.vehicle.chassisNumber)) || '—';
}

function Policies() {
    const { session, loading } = useCompanySession();
    const wallets = useTenantWallets(session && session.tenantId);
    const features = useTenantFeatures(session && session.tenantId);
    const [policies, setPolicies] = useState([]);
    const [customers, setCustomers] = useState({});
    const [ready, setReady] = useState(false);
    const [openGroups, setOpenGroups] = useState({});
    const [openId, setOpenId] = useState(null);
    const [pay, setPay] = useState({});
    const [busyId, setBusyId] = useState('');
    const [nag, setNag] = useState({});

    useEffect(() => {
        if (!session?.tenantId) return;
        Promise.all([
            getDocs(collection(db, 'tenants', session.tenantId, 'policies')),
            getDocs(collection(db, 'tenants', session.tenantId, 'customers'))
        ]).then(([pSnap, cSnap]) => {
            const byId = {};
            cSnap.forEach((d) => { byId[d.id] = { id: d.id, ...d.data() }; });
            setCustomers(byId);
            let list = [];
            pSnap.forEach((d) => list.push({ id: d.id, ...d.data() }));
            if (session.role === 'broker') {
                list = list.filter((p) => !p.brokerId || p.brokerId === session.uid);
            }
            list.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
            setPolicies(list);
            setReady(true);
        }).catch(() => setReady(true));
    }, [session]);

    const groups = useMemo(() => {
        const map = {};
        policies.forEach((p) => {
            const key = classKey(p);
            if (!map[key]) map[key] = [];
            map[key].push(p);
        });
        return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
    }, [policies]);

    const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

    const onPinUpdated = (customerId, code) => {
        setCustomers((prev) => ({
            ...prev,
            [customerId]: { ...(prev[customerId] || {}), id: customerId, lookupPin: code, pinIssued: true }
        }));
    };

    const confirmPay = async (p, customer) => {
        const state = pay[p.id] || { method: coercePaymentMethod(p.paymentMethod || defaultPaymentMethod(), features), reference: '' };
        setBusyId(p.id);
        try {
            const result = await recordPremiumPayment({
                tenantId: session.tenantId,
                session,
                policy: p,
                customer,
                wallets,
                method: state.method,
                reference: state.reference,
                activate: true
            });
            openReceiptPrint({ receipt: result.receipt, companyName: session.companyName });
            setPolicies((prev) => prev.map((row) => row.id === p.id ? { ...row, ...result.policy } : row));
        } catch (err) {
            alert(err.message || 'Payment failed');
        }
        setBusyId('');
    };

    const loadNag = async (p, customer) => {
        if (nag[p.id]) return;
        let url = window.location.origin + '/verify';
        try {
            url = verifyUrl(await attestationToken(session.tenantId, p.id));
        } catch (err) { /* ignore */ }
        const text = certificateMessage({
            companyName: session.companyName,
            name: customer.fullName || (p.policyHolder && p.policyHolder.fullName),
            plate: p.vehicle && p.vehicle.registrationNumber,
            expiry: formatDate(p.expiryDate),
            url,
            pin: customer.lookupPin
        });
        setNag((prev) => ({ ...prev, [p.id]: text }));
    };

    if (loading || !session || !ready) return <div className="p-5">Loading policies...</div>;

    return (
        <CompanyLayout session={session}>
            <p className="text-muted small mb-3">Click a class to expand. Click a contract to open the client file. IT admin can generate a new PIN for callers.</p>
            {groups.map(([key, rows]) => {
                const open = !!openGroups[key];
                return (
                    <div className="bd-card" key={key} style={{ padding: 0, overflow: 'hidden' }}>
                        <div className="bd-collapsible-header" onClick={() => toggleGroup(key)} style={{ border: 'none', borderRadius: 0 }}>
                            <div className="bd-header-left">
                                <span>{key}</span>
                                <span className="bd-count-pill">{rows.length}</span>
                            </div>
                            <span className={'bd-arrow' + (open ? ' open' : '')}>▼</span>
                        </div>
                        {open && (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="bd-table" style={{ margin: 0 }}>
                                    <thead>
                                        <tr>
                                            <th>Contract</th>
                                            <th>Client</th>
                                            <th>Premium</th>
                                            <th>Expiry</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((p) => {
                                            const expanded = openId === p.id;
                                            const holder = p.policyHolder || {};
                                            const customer = customers[p.customerId] || { id: p.customerId, phone: holder.phone, fullName: holder.fullName };
                                            return (
                                                <React.Fragment key={p.id}>
                                                    <tr className="bd-row-click" onClick={() => setOpenId(expanded ? null : p.id)}>
                                                        <td>{p.policyNumber || p.id}</td>
                                                        <td>{holder.fullName || customer.fullName || '—'}</td>
                                                        <td>D{(p.premiumAmountGMD || p.premiumAmount || 0).toLocaleString()}</td>
                                                        <td>{formatDate(p.expiryDate)}</td>
                                                        <td><span className={statusClass(p.status)}>{p.status}</span></td>
                                                    </tr>
                                                    {expanded && (
                                                        <tr className="bd-row-detail">
                                                            <td colSpan={5}>
                                                                <div className="bd-form-grid" style={{ padding: '4px 8px 12px' }}>
                                                                    <div><strong>Client</strong><div>{holder.fullName || '—'}</div></div>
                                                                    <div><strong>Phone</strong><div>{holder.phone || customer.phone || '—'}</div></div>
                                                                    <div><strong>National ID</strong><div>{holder.nationalId || '—'}</div></div>
                                                                    <div><strong>Cover</strong><div>{p.productName || p.coverageType || key}</div></div>
                                                                    <div><strong>VIN</strong><div className="font-monospace">{vinOf(p)}</div></div>
                                                                    <div><strong>Vehicle</strong><div>{p.vehicle ? [p.vehicle.make, p.vehicle.model, p.vehicle.registrationNumber].filter(Boolean).join(' ') : '—'}</div></div>
                                                                    <div><strong>In force</strong><div>{formatDate(p.effectiveDate)}</div></div>
                                                                    <div><strong>Policy expiry</strong><div>{formatDate(p.expiryDate)}</div></div>
                                                                    <div style={{ gridColumn: '1 / -1' }}>
                                                                        <strong>Approver signature</strong>
                                                                        {p.digitalSignature ? <CryptoSignaturePanel sig={p.digitalSignature} /> : <div className="text-muted">Not cryptographically signed yet.</div>}
                                                                    </div>
                                                                    {String(p.paymentStatus || '').toLowerCase() !== 'paid' && (
                                                                        <div style={{ gridColumn: '1 / -1' }} onClick={(e) => e.stopPropagation()}>
                                                                            <WalletPayBox
                                                                                wallets={wallets}
                                                                                features={features}
                                                                                policy={p}
                                                                                method={(pay[p.id] && pay[p.id].method) || coercePaymentMethod(p.paymentMethod || defaultPaymentMethod(), features)}
                                                                                reference={(pay[p.id] && pay[p.id].reference) || ''}
                                                                                onMethodChange={(method) => setPay((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), method } }))}
                                                                                onReferenceChange={(reference) => setPay((prev) => ({ ...prev, [p.id]: { ...(prev[p.id] || {}), reference } }))}
                                                                                busy={busyId === p.id}
                                                                                onConfirm={() => confirmPay(p, customer)}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    <div style={{ gridColumn: '1 / -1' }} onClick={(e) => e.stopPropagation()}>
                                                                        <button type="button" className="bd-btn bd-btn-secondary bd-btn-sm me-2" onClick={() => loadNag(p, customer)}>Prepare WhatsApp</button>
                                                                        <OutreachButtons phone={holder.phone || customer.phone} text={nag[p.id]} />
                                                                    </div>
                                                                    {isMotorPolicy(p) && String(p.status || '').toUpperCase() === 'ACTIVE' && (
                                                                        <div>
                                                                            <button
                                                                                type="button"
                                                                                className="bd-btn bd-btn-success bd-btn-sm"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openMotorCertificatePrint({
                                                                                        certificates: [printPayloadFromPolicy(p, customer)]
                                                                                    });
                                                                                }}
                                                                            >
                                                                                Print certificate
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                    <ClientPinBlock session={session} customer={customer} onUpdated={onPinUpdated} />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
        </CompanyLayout>
    );
}
export default Policies;
